<script setup>
import { computed } from 'vue';
import ChannelSelector from '../ChannelSelector.vue';

const props = defineProps({
  channel: {
    type: Object,
    required: true,
  },
  enabledFeatures: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['channelItemClick']);

const hasFbConfigured = computed(() => {
  return window.chatwootConfig?.fbAppId;
});

const hasInstagramConfigured = computed(() => {
  return window.chatwootConfig?.instagramAppId;
});

const hasTiktokConfigured = computed(() => {
  return window.chatwootConfig?.tiktokAppId;
});

const hasYoutubeConfigured = computed(() => {
  return window.chatwootConfig?.googleOAuthClientId;
});

const hasMercadoLivreConfigured = computed(() => {
  return window.chatwootConfig?.mercadoLivreAppId;
});

const isActive = computed(() => {
  const { key } = props.channel;
  if (Object.keys(props.enabledFeatures).length === 0) {
    return false;
  }
  if (key === 'website') {
    return props.enabledFeatures.channel_website;
  }
  if (key === 'facebook') {
    return props.enabledFeatures.channel_facebook && hasFbConfigured.value;
  }
  if (key === 'email') {
    return props.enabledFeatures.channel_email;
  }
  if (key === 'instagram') {
    return (
      props.enabledFeatures.channel_instagram && hasInstagramConfigured.value
    );
  }
  if (key === 'tiktok') {
    return props.enabledFeatures.channel_tiktok && hasTiktokConfigured.value;
  }
  if (key === 'youtube') {
    // Sem gate de feature por conta: feature_flags é bigint (64 bits) e
    // channel_youtube caiu na posição 70 do features.yml — o flag nunca liga.
    // O gate real é a config global do OAuth Google.
    return hasYoutubeConfigured.value;
  }
  if (key === 'mercado_livre') {
    // Mesmo padrão do youtube: sem gate de feature por conta (features.yml é
    // bigint de 64 bits, sem espaço garantido). O gate real é a config
    // global do app Mercado Livre.
    return hasMercadoLivreConfigured.value;
  }
  if (key === 'voice') {
    return props.enabledFeatures.channel_voice;
  }
  // Redes só de publicação (conexão do Estúdio Social): sempre disponíveis;
  // o módulo é cobrado na hora de conectar.
  if (key.startsWith('social_')) return true;
  return [
    'website',
    'twilio',
    'api',
    'whatsapp',
    'sms',
    'telegram',
    'line',
    'instagram',
    'tiktok',
    'youtube',
    'zernio_tiktok',
    'zernio_google',
    'zernio_linkedin',
    'rcs',
    'naviapp',
    'mercado_livre',
    'google_ads',
    'voice',
  ].includes(key);
});

const isComingSoon = computed(() => {
  const { key } = props.channel;
  // Show "Coming Soon" only if the channel is marked as coming soon
  // and the corresponding feature flag is not enabled yet.
  return ['voice'].includes(key) && !isActive.value;
});

const onItemClick = () => {
  if (isActive.value) {
    emit('channelItemClick', props.channel.key);
  }
};
</script>

<template>
  <ChannelSelector
    :title="channel.title"
    :description="channel.description"
    :icon="channel.icon"
    :is-coming-soon="isComingSoon"
    :disabled="!isActive"
    @click="onItemClick"
  />
</template>
